import { RULE_LABELS } from '@engine/index';
import { getCurrentLang, t, TRANSLATIONS } from '../lib/i18n';

function findNoKeyByValue(value, keyFilter) {
    if (!value || typeof value !== 'string') return null;
    const noMap = TRANSLATIONS?.no || {};
    for (const [key, v] of Object.entries(noMap)) {
        if (typeof v !== 'string') continue;
        if (v !== value) continue;
        if (typeof keyFilter === 'function' && !keyFilter(key)) continue;
        return key;
    }
    return null;
}

function translateKnownTemplate(value,key) {
 const source = TRANSLATIONS?.no?.[key];
 if (typeof source === 'string') return String(value).replace(source,t(key));
 if (typeof source !== 'function') return value;
 const args = Array.from({length:source.length},(_,index)=>'__AUDIT_ARG_'+index+'__');
 const template = source(...args);
 const escaped = template.replace(/[.*+?^$(){}|[\]\\]/g,'\\$&');
 const order = [...template.matchAll(/__AUDIT_ARG_(\d+)__/g)].map(match=>Number(match[1]));
 const regex = new RegExp(escaped.replace(/__AUDIT_ARG_\d+__/g,'([\\s\\S]*?)'));
 return String(value).replace(regex,(_match,...captures)=>{
  const values = [];
  order.forEach((argIndex,captureIndex)=>{values[argIndex]=captures[captureIndex];});
  if (key === 'project_pnl_allocation_note' && values[2] === TRANSLATIONS.no.project_pnl_no_conflicts) values[2] = t('project_pnl_no_conflicts');
  return t(key,...values);
 });
}
function translateStoredProjectDesc(issue) {
 if (getCurrentLang() !== 'pl' || issue.lang === 'pl') return null;
 if (!['project_pnl_summary','prosjektregnskap_terskel'].includes(issue.ruleId)) return null;
 let desc = String(issue.desc || '');
 const keys = issue.ruleId === 'prosjektregnskap_terskel'
  ? ['rule_prosjektregnskap_missing_desc','rule_prosjektregnskap_check_desc']
  : ['rule_project_pnl_desc','rule_project_pnl_partial_desc','rule_project_pnl_ok','project_pnl_allocation_note','project_pnl_prior_unknown'];
 for (const key of keys) desc = translateKnownTemplate(desc,key);
 desc = desc.replace(/(\d+) kunde\(r\) har omsetning over ([^\n]+?) \(5G eks\. mva\), men det føres ingenting på prosjekt\. For bygge- og anleggsvirksomhet kreves prosjektregnskap per prosjekt over denne terskelen \(bokføringsforskriften §8-1-3\)\. Vurder om prosjektregnskap mangler\. Terskelen gjelder per prosjekt\/kontrakt, ikke per kunde [—–-] dette er kun et screeningsignal\./,
  (_all,count,threshold)=>'Zapisana kontrola wskazała '+count+' klient(ów) z obrotem ponad '+threshold+' (5G bez VAT) i brak zapisów na projektach. Odniesienie w raporcie: ewidencja projektów dla działalności budowlanej i robót ziemnych, bokføringsforskriften §8-1-3. Sprawdź ewidencję projektów. Próg dotyczy projektu lub kontraktu, a nie klienta. To historyczny sygnał przesiewowy, nie potwierdzenie obowiązku ani braku ewidencji.');
 if (issue.ruleId === 'project_pnl_summary') {
  desc = desc.replace(/(\d+)\s+av\s+(\d+)\s+prosjekt\(er\) går i minus\s*[—–-]\s*totalt tap:\s*([^.]+)\.\s*(?:Verifiser om kostnadene er riktig allokert eller om fakturering mangler\.\s*)?/,
   (_all,losing,total,amount)=>t('rule_project_pnl_desc',losing,total,amount.trim())+' ');
  desc = desc.replace(/\bVist:/g,'Pokazano:').replace(/\bUtelatt:/g,'Pominięto:')
   .replace(/prosjekt\(er\) i minus som ikke finnes i fjorårets datagrunnlag eller har omsetning over/g,'projekt(ów) na minusie nieobecnych w danych poprzedniego roku albo z obrotem powyżej')
   .replace(/prosjekt\(er\) i minus som startet i år eller har omsetning over/g,'projekt(ów) na minusie rozpoczętych w tym roku albo z obrotem powyżej')
   .replace(/prosjekt\(er\) i minus/g,'projekt(ów) na minusie')
   .replace(/prosjekt\(er\) fra/g,'projekt(ów) z')
   .replace(/under terskelen\s*[—–-]\s*resultatet kan ligge i fjorårets fakturering/g,'poniżej progu; wynik może dotyczyć fakturowania w poprzednim roku')
   .replace(/prosjekt\(er\) går i pluss og er ikke listet/g,'projekt(ów) jest na plusie i nie ma ich na liście')
   .replace(/(\d+) i listen/g,'$1 na liście');
 }
 return desc;
}
function legacyVatDesc(issue) {
 let desc = String(issue.desc||'');
 if (getCurrentLang() === 'pl') desc = desc
  .replace(/Følgende MVA-terminer har MVA-kontoer som ikke er nullstilt:/g,'Historyczny raport wskazał niewyzerowane konta VAT dla poniższych terminów:')
  .replace(/Utgående MVA \(endring\):/g,'VAT należny (zmiana):')
  .replace(/Inngående MVA \(endring\):/g,'VAT naliczony (zmiana):')
  .replace(/Beregnet MVA-melding:/g,'Ruch netto VAT w historycznym opisie:')
  .replace(/2740 saldo:/g,'Saldo 2740:')
  .replace(/Uavstemte kontoer:/g,'Konta z saldem pozostałym:')
  .replace(/(\d+) termin\(er\) avstemt OK\./g,'Historyczny opis oznaczył $1 termin(ów) jako OK; nie potwierdza to wysyłki ani uzgodnienia z deklaracją.')
  .replace(/Ved terminens slutt \(feb, apr, jun, aug, okt, des\) skal MVA-kontoer 2700-2749, unntatt 2740, være nullstilt\. Konto 2740 skal vise beløpet til betaling\/tilgode\. Konto 2770 og 2785 er ikke MVA-kontoer i denne kontrollen\./g,'Zakres historycznej kontroli: salda kont VAT 2700-2749 poza 2740 na koniec terminu. Konto 2740 jest rozrachunkowe; konta 2770 i 2785 były poza zakresem.');
 return t('vat_legacy_scope') + (desc ? '\n\n' + desc : '');
}

function getRuleBaseKey(issue) {
    if (!issue?.ruleId) return null;
    const mapped = RULE_LABELS?.[issue.ruleId] || null;
    if (mapped) return mapped;
    return 'rule_' + issue.ruleId;
}

function extractLineValue(text, labels) {
    const src = String(text || '');
    for (const label of labels) {
        const re = new RegExp(label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*([^\\n]+)', 'i');
        const match = src.match(re);
        if (match?.[1]) return match[1].trim();
    }
    return '';
}

function extractFirstMatch(text, re) {
    const match = String(text || '').match(re);
    return match?.[1]?.trim() || '';
}

function translateDynamicDesc(issue) {
    const desc = issue?.desc || '';
    switch (issue?.ruleId) {
        case 'balance_control': {
            const sumA = extractLineValue(desc, ['Sum eiendeler (1xxx):', 'API-sum eiendeler (1xxx):', 'Suma aktywów (1xxx):', 'Suma aktywów z API (1xxx):']);
            const sumP = extractLineValue(desc, ['Sum gjeld (2xxx):', 'API-sum gjeld/egenkapital (2xxx):', 'Suma pasywów (2xxx):', 'Suma pasywów/kapitału z API (2xxx):']);
            const diff = extractLineValue(desc, ['Differanse etter resultat:', 'Differanse:', 'Różnica po wyniku:', 'Różnica:']);
            const isPartial = /API-sum|GetAggregated|rapportsum|brak pełnej sumy/i.test(String(issue.title || '') + '\n' + desc);
            if (sumA && sumP && diff) return isPartial ? t('rule_balanse_partial_desc', sumA, sumP, diff) : t('rule_balanse_error_desc', sumA, sumP, diff);
            return '';
        }
        case 'kundefordringer_vs_omsetning': {
            const kf = extractLineValue(desc, ['Kundefordringer (1500):', 'Należności od klientów (1500):']);
            const oms = extractLineValue(desc, ['Omsetning (3xxx):', 'Obrót (3xxx):']);
            const pct = extractLineValue(desc, ['Forhold:', 'Stosunek:']).replace('%', '');
            return kf && oms && pct ? t('rule_kundefordringer_desc', kf, oms, pct) : '';
        }
        case 'banklan_8150': {
            const acc = issue.details?.[0] || {};
            const accName = acc.accName || acc.name || extractFirstMatch(desc, /Konto 8150 \(([^)]+)\)/i) || t('rule_banklan_8150_fallback');
            return t('rule_banklan_8150_desc', accName);
        }
        case 'project_pnl_summary': {
            const parsed = String(desc).match(/(\d+)\s+av\s+(\d+).*?totalt tap:\s*([^.]+)\./i);
            if (!parsed) return '';
            // 11.10.2026: za zdaniem z liczbami stoi notka o zakresie karty („Vist: … Utelatt: …"),
            // której regex nie zna; odbudowany opis ją gubił (Knu Bygg, pierwszy raport po PR #24).
            // Notka zostaje w języku audytu, liczby dostają bieżący język.
            const scope = String(desc).match(/\b(?:Vist|Pokazano):[\s\S]*$/);
            return t('rule_project_pnl_desc', parsed[1], parsed[2], parsed[3].trim()) + (scope ? ` ${scope[0].trim()}` : '');
        }
        case 'likviditetsgrad': {
            const ratio = extractLineValue(desc, ['Likviditetsgrad:', 'Współczynnik płynności:']);
            const om = extractLineValue(desc, ['Omløpsmidler:', 'Aktywa obrotowe:']);
            const kg = extractLineValue(desc, ['Kortsiktig gjeld:', 'Zobowiązania krótkoterminowe:']);
            return ratio && om && kg ? t('rule_likviditet_desc', ratio, om, kg) : '';
        }
        case 'yoy_debt_increase': {
            const curr = extractLineValue(desc, ['Gjeld (2200-2999) i år:', 'Zobowiązania (2200-2999) w tym roku:']);
            const prev = extractLineValue(desc, ['Forrige år:', 'Poprzedni rok:']);
            const pct = extractLineValue(desc, ['Endring:', 'Zmiana:']).replace('%', '');
            return curr && prev && pct ? t('rule_yoy_debt_increase_desc', curr, prev, pct) : '';
        }
        case 'high_1571': {
            const amount = issue.details?.[0]?.ub != null ? String(issue.details[0].ub) : extractFirstMatch(desc, /([0-9][0-9\s.,\u00a0-]+)/);
            return amount ? t('rule_high_1571_desc', amount) : '';
        }
        case 'negativ_bank': {
            const amount = extractLineValue(desc, ['Banksaldo:', 'Saldo bankowe:']) || (issue.details?.[0]?.ub != null ? String(issue.details[0].ub) : '');
            return amount ? t('rule_neg_bank_desc', amount) : '';
        }
        case 'prior_year_not_closed': {
            const rest = extractLineValue(desc, ['Rest etter disponering:', 'Reszta po dyspozycji:']);
            const disp = extractLineValue(desc, ['Bokført disponering (88xx-89xx):', 'Zaksięgowana dyspozycja (88xx-89xx):']);
            return rest && disp ? t('rule_prior_year_not_closed_desc', rest, disp) : '';
        }
        case 'invoice_numbering': {
            const parsed = String(desc).match(/(\d+)\s+hull.*?(\d+)\s+manglende/i);
            return parsed ? t('rule_invoice_numbering_desc', parsed[1], parsed[2]) : '';
        }
        default:
            return '';
    }
}

const GENERIC_PL_DESC = {
    mva_period_check: 'Sprawdź oznaczone konta MVA dla terminu. Szczegóły i konta są w tabeli poniżej.',
    mva_per_voucher: 'Część bilagów kosztowych nie ma powiązanego księgowania MVA. Sprawdź szczegóły w tabeli poniżej.',
    aksjonaerlan_skatt: 'Saldo należności od właściciela wymaga kontroli podatkowej i oceny dokumentacji.',
    yoy_equity_decline: 'Kapitał własny istotnie spadł względem poprzedniego roku. Sprawdź sytuację kapitałową.',
    yoy_debt_increase: 'Zobowiązania istotnie wzrosły względem poprzedniego roku. Sprawdź nowe kredyty i zobowiązania.',
    tx_skipped: 'Część reguł transakcyjnych została pominięta, bo brakuje danych transakcyjnych z API.',
};

const GENERIC_DYNAMIC_PL_DESC = 'Szczegoly alertu sa w tabeli ponizej albo w danych konta. Sprawdz konto, kwote i okres, a jesli sytuacja jest poprawna, oznacz punkt jako sprawdzony z komentarzem.';

// Teksty zapisane przez bieg w języku widoku (attachIssueTranslations) mają pierwszeństwo.
export function localizedIssue(issue) {
    const lang = getCurrentLang();
    const tr = issue?.tr?.[lang];
    return tr ? { ...issue, ...tr, lang } : issue;
}

export function getActionText(issue) {
    issue = localizedIssue(issue);
    if (issue?.specialType === 'financial_result') {
        return getIssueTitle(issue);
    }
    if (issue?.actionKey) {
        const translated = t(issue.actionKey);
        if (translated !== issue.actionKey) return translated;
    }
    if (issue?.ruleId) {
        const key = 'action_' + issue.ruleId;
        const translated = t(key);
        if (translated !== key) return translated;
    }
    const reverseActionKey = findNoKeyByValue(issue?.action, (k) => k.startsWith('action_'));
    if (reverseActionKey) {
        const translated = t(reverseActionKey);
        if (translated !== reverseActionKey) return translated;
    }
    return issue?.action || getIssueTitle(issue);
}

export function getIssueTitle(issue) {
    if (!issue) return '';
    issue = localizedIssue(issue);
    if (issue.ruleId === 'duplicate_entries' && issue.specialType === 'review_signal') return t('duplicates_weak_title');
    if (issue.specialType === 'rule_not_checked') return issue.title || '';

    if (issue.specialType === 'financial_result') {
        const key = issue.type === 'success' ? 'rule_financial_profit' : 'rule_financial_loss';
        const translated = t(key);
        if (translated !== key) return translated;
    }

    if (issue.ruleId === 'amortization_12xx' && issue.type === 'error') {
        const translated = t('rule_amort_system_error');
        if (translated !== 'rule_amort_system_error') return translated;
    }

    if (issue.ruleId === 'balance_control') {
        const isPartial = /mangler full|full rapportsum|brak pełnej|API-sum|GetAggregated/i.test(String(issue.title || '') + '\n' + String(issue.desc || ''));
        const key = isPartial ? 'rule_balanse_partial' : (issue.type === 'success' ? 'rule_balanse_ok' : 'rule_balanse_error');
        const translated = t(key);
        if (translated !== key) return translated;
    }

    const baseKey = getRuleBaseKey(issue);
    if (baseKey) {
        const titleKey = baseKey + '_title';
        const translatedTitle = t(titleKey);
        if (translatedTitle !== titleKey) return translatedTitle;

        const translatedBase = t(baseKey);
        if (translatedBase !== baseKey) return translatedBase;
    }

    const reverseRuleKey = findNoKeyByValue(issue.title, (k) => k.startsWith('rule_'));
    if (reverseRuleKey) {
        const translated = t(reverseRuleKey);
        if (translated !== reverseRuleKey) return translated;
    }
    return issue.title || '';
}

export function getIssueDesc(issue) {
    if (!issue) return '';
    issue = localizedIssue(issue);
    if (issue.specialType === 'tx_verified_empty') return t('rule_tx_verified_empty_desc');
    if (issue.ruleId === 'mva_period_check' && !(Array.isArray(issue.evidence?.terms) && issue.evidence.terms.length && issue.evidence.terms.every(row => Array.isArray(row?.accountBalances) && Object.hasOwn(row,'ledgerCleared')))) return legacyVatDesc(issue);
    const storedProjectDesc = translateStoredProjectDesc(issue);
    if (storedProjectDesc !== null) return storedProjectDesc;
    if (issue.specialType === 'financial_result') {
        const amountMatch = String(issue.desc || '').match(/:\s*(.+?)\.?$/);
        if (amountMatch?.[1]) {
            const translated = t('rule_financial_desc', amountMatch[1]);
            if (translated !== 'rule_financial_desc') return translated;
        }
    }

    // Scoped success text must never fall through to a generic warning description.
    if (issue.type === 'success' || ['rule_not_checked', 'review_signal', 'screened_ok', 'coverage'].includes(issue.specialType)) return issue.desc || '';
    // 11.10.2026: raport przeliczony w języku widoku ma już właściwy opis z liczbami; do tej pory
    // karty z dynamicznym opisem (duplikaty, kostnadstopp, husleie, MVA-termin) dostawały po polsku
    // ogólnik „Szczegoly alertu sa w tabeli ponizej", choć silnik policzył je po polsku.
    if (issue.lang && getCurrentLang && issue.lang === getCurrentLang()) return issue.desc || '';

    if (issue.ruleId === 'amortization_12xx' && issue.type === 'error') {
        const translated = t('rule_amort_system_error_desc');
        if (translated !== 'rule_amort_system_error_desc') return translated;
    }

    const dynamicDesc = translateDynamicDesc(issue);
    if (dynamicDesc) return dynamicDesc;
    if (issue.evidence) return issue.desc || '';

    const baseKey = getRuleBaseKey(issue);
    if (baseKey) {
        const descKey = baseKey + '_desc';
        const noDescTemplate = TRANSLATIONS?.no?.[descKey];
        const altDescKey = noDescTemplate == null && baseKey.endsWith('_label')
            ? baseKey.replace(/_label$/, '_desc')
            : null;
        const noAltDescTemplate = altDescKey ? TRANSLATIONS?.no?.[altDescKey] : null;
        if (typeof noDescTemplate !== 'function') {
            const translatedDesc = t(descKey);
            if (translatedDesc !== descKey) return translatedDesc;
            if (altDescKey && typeof noAltDescTemplate !== 'function') {
                const translatedAltDesc = t(altDescKey);
                if (translatedAltDesc !== altDescKey) return translatedAltDesc;
            }
        } else if (getCurrentLang && getCurrentLang() === 'pl') {
            return GENERIC_PL_DESC[issue.ruleId] || GENERIC_DYNAMIC_PL_DESC;
        }
        if (typeof noAltDescTemplate === 'function' && getCurrentLang && getCurrentLang() === 'pl') {
            return GENERIC_PL_DESC[issue.ruleId] || GENERIC_DYNAMIC_PL_DESC;
        }
    }

    const reverseDescKey = findNoKeyByValue(issue.desc, (k) => k.startsWith('rule_') && k.endsWith('_desc'));
    if (reverseDescKey) {
        const translated = t(reverseDescKey);
        if (translated !== reverseDescKey) return translated;
    }
    if (getCurrentLang && getCurrentLang() === 'pl' && GENERIC_PL_DESC[issue.ruleId]) {
        return GENERIC_PL_DESC[issue.ruleId];
    }
    return issue.desc || '';
}
